import React, { useEffect, useMemo, useRef, useState } from 'react';
// @ts-ignore
import Plotly from 'plotly.js-dist-min';

type DiseaseId = 'FMD' | 'LSD' | 'SGP' | 'PPR';
type SpeciesId = 'cattle' | 'sheep' | 'goat' | 'pig' | 'buffalo' | 'wild';

const DISEASES: { id: DiseaseId; label: string }[] = [
  { id: 'FMD', label: 'FMD' },
  { id: 'LSD', label: 'LSD' },
  { id: 'SGP', label: 'SGP' },
  { id: 'PPR', label: 'PPR' },
];

/** Species relevant to serological surveillance per disease */
const DISEASE_SPECIES: Record<
  DiseaseId,
  { id: SpeciesId; label: string; census: string | null; tested: string; pos: string }[]
> = {
  FMD: [
    { id: 'cattle', label: 'Cattle', census: 'cattle', tested: 'cattle_tested', pos: 'cattle_sero_fmd' },
    { id: 'sheep', label: 'Sheep', census: 'sheep', tested: 'sheep_tested', pos: 'sheep_sero_fmd' },
    { id: 'goat', label: 'Goats', census: 'goat', tested: 'goat_tested', pos: 'goat_sero_fmd' },
    { id: 'pig', label: 'Pigs', census: 'pig', tested: 'pig_tested', pos: 'pig_sero_fmd' },
    { id: 'buffalo', label: 'Buffaloes', census: 'buffalo', tested: 'buffalo_tested', pos: 'buffalo_sero_fmd' },
    { id: 'wild', label: 'Wild', census: null, tested: 'wild_tested', pos: 'wild_sero_fmd' },
  ],
  LSD: [
    { id: 'cattle', label: 'Cattle', census: 'cattle', tested: 'cattle_tested', pos: 'cattle_sero_lsd' },
    { id: 'buffalo', label: 'Buffaloes', census: 'buffalo', tested: 'buffalo_tested', pos: 'buffalo_sero_lsd' },
  ],
  SGP: [
    { id: 'sheep', label: 'Sheep', census: 'sheep', tested: 'sheep_tested', pos: 'sheep_sero_sgp' },
    { id: 'goat', label: 'Goats', census: 'goat', tested: 'goat_tested', pos: 'goat_sero_sgp' },
  ],
  PPR: [
    { id: 'sheep', label: 'Sheep', census: 'sheep', tested: 'sheep_tested', pos: 'sheep_sero_ppr' },
    { id: 'goat', label: 'Goats', census: 'goat', tested: 'goat_tested', pos: 'goat_sero_ppr' },
  ],
};

const COLORS = {
  positive: '#f5b4b4',
  testedNeg: '#a8d4ef',
  notTested: '#e5e7eb',
};

function n(v: unknown): number {
  if (v === null || v === undefined || v === '') return 0;
  const x = Number(v);
  return Number.isFinite(x) && x > 0 ? x : 0;
}

interface NestRow {
  label: string;
  area: string;
  species: string;
  census: number;
  tested: number;
  positive: number;
  testedNeg: number;
  notTested: number;
}

function buildNestRows(serology: any[], disease: DiseaseId): NestRow[] {
  const specs = DISEASE_SPECIES[disease];
  const out: NestRow[] = [];
  for (const row of serology || []) {
    const area = String(row.district_province || row.district || '—');
    for (const sp of specs) {
      const censusRaw = sp.census ? n(row[sp.census]) : 0;
      let tested = n(row[sp.tested]);
      let positive = n(row[sp.pos]);
      // Wild has no census — whole bar = tested (or positives if that's all we have)
      const census = sp.census ? censusRaw : Math.max(tested, positive);
      if (tested > census && census > 0) tested = census;
      if (positive > tested) positive = tested;
      if (census <= 0 && tested <= 0 && positive <= 0) continue;
      const total = Math.max(census, tested, positive);
      const testedCap = Math.min(tested, total);
      const posCap = Math.min(positive, testedCap);
      out.push({
        label: `${area} · ${sp.label}`,
        area,
        species: sp.label,
        census: total,
        tested: testedCap,
        positive: posCap,
        testedNeg: Math.max(0, testedCap - posCap),
        notTested: Math.max(0, total - testedCap),
      });
    }
  }
  return out;
}

export interface CycleSerologyVizProps {
  serology: any[];
  quarter: number;
  year: number;
}

const CycleSerologyViz: React.FC<CycleSerologyVizProps> = ({ serology, quarter, year }) => {
  const [disease, setDisease] = useState<DiseaseId>('FMD');
  const chartRef = useRef<HTMLDivElement | null>(null);

  const nestRows = useMemo(() => buildNestRows(serology, disease), [serology, disease]);

  useEffect(() => {
    const el = chartRef.current;
    if (!el) return;

    if (!nestRows.length) {
      Plotly.purge(el);
      return;
    }

    const y = nestRows.map((r) => r.label);
    const hoverBase = nestRows.map((r) => {
      const testedPct = r.census > 0 ? ((r.tested / r.census) * 100).toFixed(1) : '—';
      const posPctTested = r.tested > 0 ? ((r.positive / r.tested) * 100).toFixed(1) : '—';
      const posPctAll = r.census > 0 ? ((r.positive / r.census) * 100).toFixed(1) : '—';
      return (
        `<b>${r.area}</b> · ${r.species}<br>` +
        `Census: ${r.census.toLocaleString()}<br>` +
        `Tested: ${r.tested.toLocaleString()} (${testedPct}% of census)<br>` +
        `Sero + (${disease}): ${r.positive.toLocaleString()} (${posPctTested}% of tested, ${posPctAll}% of census)`
      );
    });

    const traces = [
      {
        type: 'bar',
        orientation: 'h',
        name: `Sero + (${disease})`,
        y,
        x: nestRows.map((r) => r.positive),
        marker: { color: COLORS.positive, line: { color: '#fff', width: 1 } },
        hoverinfo: 'text',
        hovertext: hoverBase,
        text: nestRows.map((r) => (r.positive > 0 ? String(r.positive) : '')),
        textposition: 'inside',
        insidetextanchor: 'middle',
        textfont: { size: 11, color: '#374151' },
      },
      {
        type: 'bar',
        orientation: 'h',
        name: 'Tested (sero −)',
        y,
        x: nestRows.map((r) => r.testedNeg),
        marker: { color: COLORS.testedNeg, line: { color: '#fff', width: 1 } },
        hoverinfo: 'text',
        hovertext: hoverBase,
        text: nestRows.map((r) => (r.testedNeg > 0 ? String(r.testedNeg) : '')),
        textposition: 'inside',
        insidetextanchor: 'middle',
        textfont: { size: 11, color: '#374151' },
      },
      {
        type: 'bar',
        orientation: 'h',
        name: 'Not tested',
        y,
        x: nestRows.map((r) => r.notTested),
        marker: { color: COLORS.notTested, line: { color: '#fff', width: 1 } },
        hoverinfo: 'text',
        hovertext: hoverBase,
        text: nestRows.map((r) => (r.notTested > 0 ? String(r.notTested) : '')),
        textposition: 'inside',
        insidetextanchor: 'middle',
        textfont: { size: 11, color: '#6b7280' },
      },
    ];

    const rowH = 28;
    const height = Math.max(280, 80 + nestRows.length * rowH);

    Plotly.newPlot(
      el,
      traces,
      {
        barmode: 'stack',
        margin: { t: 16, r: 24, b: 48, l: 160 },
        height,
        paper_bgcolor: 'rgba(0,0,0,0)',
        plot_bgcolor: 'rgba(0,0,0,0)',
        font: { size: 12, color: '#374151' },
        xaxis: {
          title: { text: 'Number of animals (full bar is the census)', font: { size: 12 } },
          gridcolor: '#f3f4f6',
          zeroline: false,
        },
        yaxis: {
          autorange: 'reversed',
          tickfont: { size: 11 },
        },
        legend: {
          orientation: 'h',
          y: -0.12,
          x: 0,
          font: { size: 11 },
        },
        bargap: 0.25,
      },
      { responsive: true, displayModeBar: false },
    );

    return () => {
      try {
        Plotly.purge(el);
      } catch {
        /* ignore */
      }
    };
  }, [nestRows, disease]);

  return (
    <div className="space-y-4">
      <h3 className="text-2xl font-black text-gray-800 bg-green-100 px-4 py-3 rounded">
        Serological Examination — Quarter {quarter}, {year}
      </h3>

      <p className="text-sm text-gray-600 leading-relaxed max-w-3xl">
        The full length of each bar is the number of animals present (census). Within that, the
        coloured segments show how many were tested, and within the tested animals, how many were
        serologically positive for the selected disease. The three segments always add up to the
        census. Wild animals have no census figure, so for wild the full bar is the number tested.
      </p>

      <div className="flex flex-wrap gap-2">
        {DISEASES.map((d) => (
          <button
            key={d.id}
            type="button"
            onClick={() => setDisease(d.id)}
            className={`nav-btn px-4 py-1.5 text-sm ${
              disease === d.id ? 'ring-2 ring-green-600 ring-offset-1' : 'opacity-80'
            }`}
          >
            {d.label}
          </button>
        ))}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-3">
        <div className="flex flex-wrap gap-4 mb-2 text-xs text-gray-600">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: COLORS.positive }} />
            Sero + ({disease})
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: COLORS.testedNeg }} />
            Tested, sero −
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span
              className="w-3 h-3 rounded-sm border border-gray-200"
              style={{ backgroundColor: COLORS.notTested }}
            />
            Not tested
          </span>
        </div>

        {nestRows.length ? (
          <div ref={chartRef} style={{ width: '100%' }} />
        ) : (
          <div className="h-48 flex items-center justify-center text-sm text-gray-500">
            No serological census / tested data for {disease} in this quarter.
          </div>
        )}
      </div>
    </div>
  );
};

export default CycleSerologyViz;
